// Shared glue for flattening a premium (gated) retelling payload into a
// DocumentModel with the SAME pure flattener build-corpus uses — a premium
// chapter must flatten identically to a free one. The dynamic import keeps
// the remark/tokenizer weight out of the free reader bundle; only an
// entitled premium reader (or the admin workbench) pays for it (plan 10
// ADR-1). Cached promises for the session; `use()` unwraps them under
// Suspense.

import { getBookConfig } from "@bindery/convex-retells/model/bookConfig";
import type { Block } from "@bindery/convex-retells/model/block";
import type { ContextSeedLink } from "@bindery/shared/corpus/context-seed";
import type { FlattenedDocument } from "@bindery/corpus-pipeline/ingest/toDocumentModel";

type FlattenModule =
  typeof import("@bindery/corpus-pipeline/ingest/toDocumentModel");

export type Flattener = FlattenModule["retellingToDocumentModel"];
export type MatterFlattener = FlattenModule["matterToDocumentModel"];

// ONE cached module promise behind both loaders. They pull two functions out of
// the same chunk, so a second `import()` would be a second promise for a module
// already resolved.
let modulePromise: Promise<FlattenModule> | null = null;

function loadModule(): Promise<FlattenModule> {
  modulePromise ??= import("@bindery/corpus-pipeline/ingest/toDocumentModel");
  return modulePromise;
}

// The DERIVED promises are cached too, not just the module — caching only the
// `import()` is not enough. `use()` recognizes a promise by IDENTITY: hand it a
// fresh `.then()` chain on every render and it can only treat that unfamiliar
// object as pending, so each render re-suspends the boundary and the reader (or
// workbench) pane never settles. Caching the settled promise object means the
// re-render `use()`s the same reference and reads the value straight through.
// Same reason `corpus.ts` caches promises rather than resolved values.
let flattenerPromise: Promise<Flattener> | null = null;
let matterFlattenerPromise: Promise<MatterFlattener> | null = null;

/** Dynamically import the retelling flattener, caching the promise. */
export function loadFlattener(): Promise<Flattener> {
  flattenerPromise ??= loadModule().then((m) => m.retellingToDocumentModel);
  return flattenerPromise;
}

/**
 * Dynamically import the MATTER flattener — the same chunk, a different entry
 * point. Matter's heading is its bare title, so it takes neither a chapter
 * number nor a `BookConfig`, and there is no `flattenMatter` wrapper beside
 * `flattenChapter` below because there is nothing left to wrap.
 */
export function loadMatterFlattener(): Promise<MatterFlattener> {
  matterFlattenerPromise ??= loadModule().then((m) => m.matterToDocumentModel);
  return matterFlattenerPromise;
}

const NO_CONTEXT_LINKS: readonly ContextSeedLink[] = [];

/**
 * Flatten a chapter's title/blocks into a DocumentModel, placing its context
 * links. A link whose anchor no longer resolves comes back in `unresolved`.
 */
export function flattenChapter(
  flatten: Flattener,
  args: {
    bookSlug: string;
    chapterNumber: number;
    title: string;
    blocks: Block[];
    contextLinks?: readonly ContextSeedLink[];
  },
): FlattenedDocument {
  const config = getBookConfig(args.bookSlug);
  return flatten(
    {
      chapterNumber: args.chapterNumber,
      title: args.title,
      blocks: args.blocks,
    },
    config,
    args.contextLinks ?? NO_CONTEXT_LINKS,
  );
}
